// src/pages/admin/AdminShared.tsx
// Shared UI atoms used across all admin pages.

import React from "react";

import { ArrowLeft, ArrowRight } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import Ico from "../../components/Ico";
/* ─── DESIGN TOKENS (mirror design system) ───────────────────────── */
export const C = {
  primary: "#1b2d5e",
  coral: "#efb0a4",
  dark: "#16213e",
  bg: "#f1f3f8",
  border: "#dde2ec",
  muted: "#8189a0",
  green: "#6cc2a0",
  amber: "#f0a050",
  red: "#e57373",
};

/* ─── STATUS CHIPS ───────────────────────────────────────────────── */

const ENROL_STATUS: Record<
  string,
  { label: string; bg: string; text: string; border: string }
> = {
  pending: {
    label: "Pending payment",
    bg: "#fff8ee",
    text: "#7a4a10",
    border: "#f5d9a0",
  },
  confirmed: {
    label: "Confirmed",
    bg: "#edfaf4",
    text: "#2a7a56",
    border: "#b6e8d0",
  },
  completed: {
    label: "Completed",
    bg: "#ebeff7",
    text: "#4a44b0",
    border: "#cdd4e2",
  },
  withdrawn: {
    label: "Withdrawn",
    bg: "#fff3f3",
    text: "#9b2a2a",
    border: "#f5c0c0",
  },
};

const PAY_STATUS: Record<
  string,
  { label: string; bg: string; text: string; border: string; dot: string }
> = {
  paid: {
    label: "Paid",
    bg: "#edfaf4",
    text: "#2a7a56",
    border: "#b6e8d0",
    dot: "#6cc2a0",
  },
  pending: {
    label: "Pending",
    bg: "#fff8ee",
    text: "#7a4a10",
    border: "#f5d9a0",
    dot: "#f0a050",
  },
  failed: {
    label: "Failed",
    bg: "#fff3f3",
    text: "#9b2a2a",
    border: "#f5c0c0",
    dot: "#e57373",
  },
  refunded: {
    label: "Refunded",
    bg: "#ebeff7",
    text: "#4a44b0",
    border: "#cdd4e2",
    dot: "#1b2d5e",
  },
  overdue: {
    label: "Overdue",
    bg: "#fff3f3",
    text: "#9b2a2a",
    border: "#f5c0c0",
    dot: "#e57373",
  },
  waived: {
    label: "Waived",
    bg: "#ebeff7",
    text: "#4a44b0",
    border: "#cdd4e2",
    dot: "#1b2d5e",
  },
};

const STUDENT_STATUS: Record<
  string,
  { label: string; bg: string; text: string; border: string }
> = {
  prospect: {
    label: "Prospect",
    bg: "#f1f3f8",
    text: "#8189a0",
    border: "#dde2ec",
  },
  enrolled: {
    label: "Enrolled",
    bg: "#ebeff7",
    text: "#4a44b0",
    border: "#cdd4e2",
  },
  active: {
    label: "Active",
    bg: "#edfaf4",
    text: "#2a7a56",
    border: "#b6e8d0",
  },
  inactive: {
    label: "Inactive",
    bg: "#fff3f3",
    text: "#9b2a2a",
    border: "#f5c0c0",
  },
};

const TRACK_COLORS: Record<
  string,
  { bg: string; text: string; border: string; label: string; ageRange?: string }
> = {
  foundation_builders: {
    bg: "#fff4f1",
    text: "#7a3020",
    border: "#f5c5b0",
    label: "Foundation (6–9)",
    ageRange: "6–9",
  },
  explorers: {
    bg: "#ebeff7",
    text: "#4a44b0",
    border: "#cdd4e2",
    label: "Explorers (9–12)",
  },
  innovators: {
    bg: "#e8ecf5",
    text: "#1b2d5e",
    border: "#c9cfe0",
    label: "Innovators (13–18)",
  },
  unassigned: {
    bg: "#f1f3f8",
    text: "#8189a0",
    border: "#dde2ec",
    label: "Unassigned",
  },
};

const BILLING_COLORS: Record<
  string,
  { bg: string; text: string; border: string }
> = {
  monthly: { bg: "#fff8ee", text: "#7a4a10", border: "#f5d9a0" },
  quarterly: { bg: "#ebeff7", text: "#4a44b0", border: "#cdd4e2" },
  annually: { bg: "#edfaf4", text: "#2a7a56", border: "#b6e8d0" },
};

const RATING_COLORS: Record<string, string> = {
  emerging: "#f0a050",
  developing: "#1b2d5e",
  strong: "#6cc2a0",
  excellent: "#2a7a56",
};

const ATTENDANCE_CONFIG: Record<
  string,
  { label: string; bg: string; text: string; border: string }
> = {
  present: {
    label: "Present",
    bg: "#edfaf4",
    text: "#2a7a56",
    border: "#b6e8d0",
  },
  late: { label: "Late", bg: "#fff8ee", text: "#7a4a10", border: "#f5d9a0" },
  absent: {
    label: "Absent",
    bg: "#fff3f3",
    text: "#9b2a2a",
    border: "#f5c0c0",
  },
  excused: {
    label: "Excused",
    bg: "#f1f3f8",
    text: "#8189a0",
    border: "#dde2ec",
  },
};

/* ─── CHIP COMPONENTS ────────────────────────────────────────────── */

export function EnrolChip({ status }: { status?: string | null }) {
  const c = ENROL_STATUS[status ?? ""] ?? ENROL_STATUS.pending;
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[12.5px] font-semibold"
      style={{
        background: c.bg,
        color: c.text,
        border: `1px solid ${c.border}`,
      }}
    >
      {c.label}
    </span>
  );
}

export function PayChip({ status }: { status?: string | null }) {
  const c = PAY_STATUS[status ?? ""] ?? PAY_STATUS.pending;
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[12.5px] font-semibold"
      style={{
        background: c.bg,
        color: c.text,
        border: `1px solid ${c.border}`,
      }}
    >
      <span
        className="h-1.5 w-1.5 rounded-full"
        style={{ background: c.dot }}
      />
      {c.label}
    </span>
  );
}

export function StudentChip({ status }: { status?: string | null }) {
  const c = STUDENT_STATUS[status ?? ""] ?? STUDENT_STATUS.prospect;
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[12.5px] font-semibold"
      style={{
        background: c.bg,
        color: c.text,
        border: `1px solid ${c.border}`,
      }}
    >
      {c.label}
    </span>
  );
}

export function TrackChip({ track }: { track?: string | null }) {
  const c = TRACK_COLORS[track ?? ""] ?? TRACK_COLORS.unassigned;
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[12.5px] font-semibold"
      style={{
        background: c.bg,
        color: c.text,
        border: `1px solid ${c.border}`,
      }}
    >
      {c.label}
    </span>
  );
}

export function BillingChip({
  period,
  label,
}: {
  period: string | null;
  label?: string | null;
}) {
  if (!period) return null;
  const c = BILLING_COLORS[period] ?? BILLING_COLORS.monthly;
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-[11.5px] font-bold"
      style={{
        background: c.bg,
        color: c.text,
        border: `1px solid ${c.border}`,
      }}
    >
      {label ?? period}
    </span>
  );
}

export function RatingChip({ rating }: { rating?: string | null }) {
  if (!rating) return null;
  const color = RATING_COLORS[rating] ?? C.primary;
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[12.5px] font-bold"
      style={{
        background: color + "18",
        color,
        border: `1px solid ${color}40`,
      }}
    >
      {rating.charAt(0).toUpperCase() + rating.slice(1)}
    </span>
  );
}

export function AttendanceChip({ status }: { status?: string | null }) {
  const c = ATTENDANCE_CONFIG[status ?? ""] ?? ATTENDANCE_CONFIG.absent;
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[12.5px] font-semibold"
      style={{
        background: c.bg,
        color: c.text,
        border: `1px solid ${c.border}`,
      }}
    >
      {c.label}
    </span>
  );
}

/* ─── LAYOUT ATOMS ───────────────────────────────────────────────── */

export function Card({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`rounded-[22px] border border-[#e5e8ef] bg-white shadow-[0_4px_16px_rgba(27,45,94,0.05)] ${className}`}
    >
      {children}
    </div>
  );
}

export function PageHeader({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div>
        <h1 className="text-[1.6rem] font-bold tracking-[-0.04em] text-[#16213e]">
          {title}
        </h1>
        {subtitle && <p className="mt-1 text-sm text-[#5f6780]">{subtitle}</p>}
      </div>
      {action && <div className="flex-shrink-0 self-start">{action}</div>}
    </div>
  );
}

export function Skeleton({ className = "" }: { className?: string }) {
  return (
    <div className={`animate-pulse rounded-xl bg-[#ebeff7] ${className}`} />
  );
}

export function Spinner({ size = "md" }: { size?: "sm" | "md" | "lg" }) {
  const s = { sm: "h-4 w-4", md: "h-6 w-6", lg: "h-10 w-10" }[size];
  return (
    <svg
      className={`${s} animate-spin text-[#1b2d5e]`}
      viewBox="0 0 24 24"
      fill="none"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z"
      />
    </svg>
  );
}

export function EmptyState({
  icon,
  title,
  subtitle,
  action,
}: {
  icon: LucideIcon;
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center rounded-[28px] border border-dashed border-[#cdd4e2] bg-[#f9fafc] px-6 py-14 text-center">
      <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#ebeff7] text-3xl">
        <Ico icon={icon} />
      </span>
      <p className="mt-5 text-base font-bold text-[#16213e]">{title}</p>
      {subtitle && (
        <p className="mt-2 max-w-xs text-sm leading-6 text-[#5f6780]">
          {subtitle}
        </p>
      )}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function StatCard({
  label,
  value,
  sub,
  icon,
  color,
  bg,
}: {
  label: string;
  value: string;
  sub?: string;
  icon: LucideIcon;
  color: string;
  bg: string;
}) {
  return (
    <div className="flex items-center gap-4 rounded-[20px] border border-[#e5e8ef] bg-white px-5 py-4 shadow-[0_2px_8px_rgba(27,45,94,0.04)]">
      <span
        className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-[12px] text-lg"
        style={{ background: bg, color }}
      >
        <Ico icon={icon} />
      </span>
      <div className="min-w-0">
        <p className="text-[11.5px] font-bold uppercase tracking-wider text-[#8189a0]">
          {label}
        </p>
        <p className="mt-0.5 text-xl font-bold tracking-[-0.03em] text-[#16213e]">
          {value}
        </p>
        {sub && <p className="text-[12.5px] text-[#a6adbf]">{sub}</p>}
      </div>
    </div>
  );
}

export function TableContainer({ children }: { children: React.ReactNode }) {
  return (
    <div className="overflow-hidden rounded-[22px] border border-[#e5e8ef] bg-white shadow-[0_4px_16px_rgba(27,45,94,0.05)]">
      <div className="overflow-x-auto">{children}</div>
    </div>
  );
}

export function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="px-5 py-3.5 text-left text-[11.5px] font-bold uppercase tracking-wider text-[#8189a0]">
      {children}
    </th>
  );
}

export function Td({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <td className={`px-5 py-3.5 text-sm text-[#3b4560] ${className}`}>
      {children}
    </td>
  );
}

export function Avatar({
  firstName,
  lastName,
  size = "md",
  className = "",
}: {
  firstName?: string | null;
  lastName?: string | null;
  size?: "sm" | "md" | "lg";
  className?: string;
}) {
  const s =
    size === "sm"
      ? "h-7 w-7 text-[11.5px] rounded-[8px]"
      : size === "lg"
        ? "h-12 w-12 text-sm rounded-[12px]"
        : "h-9 w-9 text-xs rounded-[10px]";
  const initials =
    `${(firstName ?? "?").charAt(0)}${(lastName ?? "").charAt(0)}`
      .toUpperCase()
      .trim() || "?";
  return (
    <span
      className={`flex flex-shrink-0 items-center justify-center bg-[#ebeff7] font-bold text-[#1b2d5e] ${s} ${className}`}
    >
      {initials}
    </span>
  );
}

export function Pagination({
  page,
  pages,
  total,
  count,
  onPage,
}: {
  page: number;
  pages: number;
  total: number;
  count: number;
  onPage: (p: number) => void;
}) {
  if (pages <= 1) return null;
  return (
    <div className="flex items-center justify-between border-t border-[#ebeff7] px-5 py-3.5">
      <p className="text-xs text-[#8189a0]">
        Showing {count} of {total}
      </p>
      <div className="flex items-center gap-1.5">
        <button
          onClick={() => onPage(page - 1)}
          disabled={page === 1}
          className="rounded-xl border border-[#dde2ec] px-3 py-1.5 text-xs font-semibold text-[#3b4560] hover:bg-[#f1f3f8] disabled:opacity-40"
        >
         {" "}<ArrowLeft className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />
        </button>
        {Array.from({ length: Math.min(pages, 5) }, (_, i) => i + 1).map(
          (p) => (
            <button
              key={p}
              onClick={() => onPage(p)}
              className={`rounded-xl px-3 py-1.5 text-xs font-bold transition ${p === page ? "bg-[#1b2d5e] text-white" : "border border-[#dde2ec] text-[#3b4560] hover:bg-[#f1f3f8]"}`}
            >
              {p}
            </button>
          ),
        )}
        <button
          onClick={() => onPage(page + 1)}
          disabled={page === pages}
          className="rounded-xl border border-[#dde2ec] px-3 py-1.5 text-xs font-semibold text-[#3b4560] hover:bg-[#f1f3f8] disabled:opacity-40"
        >
         {" "}<ArrowRight className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />
        </button>
      </div>
    </div>
  );
}

export function SearchInput({
  value,
  onChange,
  placeholder = "Search…",
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <div className="relative">
      <svg
        className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#a6adbf]"
        viewBox="0 0 16 16"
        fill="none"
      >
        <circle
          cx="6.5"
          cy="6.5"
          r="4.5"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <path
          d="M10 10l3 3"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-xl border border-[#dde2ec] bg-white py-2 pl-8 pr-3 text-xs text-[#16213e] placeholder:text-[#a6adbf] outline-none transition focus:border-[#1b2d5e]"
      />
    </div>
  );
}

export function Select({
  value,
  onChange,
  children,
  className = "",
}: {
  value: string;
  onChange: (v: string) => void;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`rounded-xl border border-[#dde2ec] bg-white px-3 py-2 text-xs font-semibold text-[#3b4560] outline-none focus:border-[#1b2d5e] cursor-pointer ${className}`}
    >
      {children}
    </select>
  );
}

export function Button({
  children,
  onClick,
  disabled,
  variant = "primary",
  size = "md",
  className = "",
}: {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  variant?: "primary" | "secondary" | "danger" | "ghost";
  size?: "sm" | "md";
  className?: string;
}) {
  const base =
    "inline-flex items-center justify-center gap-2 font-semibold rounded-2xl transition disabled:opacity-50 disabled:cursor-not-allowed";
  const sizes = { sm: "px-3 py-2 text-xs", md: "px-5 py-2.5 text-sm" };
  const variants = {
    primary:
      "bg-[#1b2d5e] text-white shadow-[0_8px_24px_rgba(27,45,94,0.28)] hover:bg-[#24397a]",
    secondary: "border border-[#dde2ec] text-[#3b4560] hover:bg-[#f1f3f8]",
    danger:
      "bg-[#fff3f3] text-[#9b2a2a] border border-[#f5c0c0] hover:bg-[#ffe8e8]",
    ghost: "text-[#8189a0] hover:text-[#16213e] hover:bg-[#f1f3f8]",
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${base} ${sizes[size]} ${variants[variant]} ${className}`}
    >
      {children}
    </button>
  );
}

/* ─── HELPERS ────────────────────────────────────────────────────── */
export function formatNaira(n: number | null | undefined) {
  return n != null ? `₦${Number(n).toLocaleString("en-NG")}` : "—";
}
export function formatDate(iso: string | null | undefined) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}
export function trackConfig(track?: string | null) {
  return TRACK_COLORS[track ?? ""] ?? TRACK_COLORS.unassigned;
}
